import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

export default function Cursor() {
    const dotRef = useRef(null);
    const ringRef = useRef(null);
    const [useCustomCursor, setUseCustomCursor] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia('(pointer: fine)');
        const sync = () => setUseCustomCursor(mq.matches);
        sync();
        mq.addEventListener('change', sync);
        return () => mq.removeEventListener('change', sync);
    }, []);

    useEffect(() => {
        if (!useCustomCursor) return;

        const onMouseMove = (e) => {
            gsap.to(dotRef.current, {
                x: e.clientX,
                y: e.clientY,
                duration: 0,
                ease: "none"
            });
            gsap.to(ringRef.current, {
                x: e.clientX,
                y: e.clientY,
                duration: 0.15,
                ease: "power2.out"
            });
        };

        const onMouseEnterLink = () => {
            gsap.to(ringRef.current, { scale: 2.5, opacity: 0.5, duration: 0.3, ease: "power2.out" });
            gsap.to(dotRef.current, { scale: 0, duration: 0.2 });
        };

        const onMouseLeaveLink = () => {
            gsap.to(ringRef.current, { scale: 1, opacity: 1, duration: 0.3, ease: "power2.out" });
            gsap.to(dotRef.current, { scale: 1, duration: 0.2 });
        };

        window.addEventListener('mousemove', onMouseMove);

        // Add listeners to all interactable elements
        const attachListeners = () => {
            const interactables = document.querySelectorAll('a, button, input, select, textarea');
            interactables.forEach(el => {
                el.addEventListener('mouseenter', onMouseEnterLink);
                el.addEventListener('mouseleave', onMouseLeaveLink);
            });
        };

        attachListeners();

        // MutationObserver to attach listeners to newly added elements (e.g. dynamic content)
        const observer = new MutationObserver((mutations) => {
            let shouldReattach = false;
            mutations.forEach((mutation) => {
                if (mutation.addedNodes.length) shouldReattach = true;
            });
            if (shouldReattach) {
                // Remove old first to prevent duplicates
                const interactables = document.querySelectorAll('a, button, input, select, textarea');
                interactables.forEach(el => {
                    el.removeEventListener('mouseenter', onMouseEnterLink);
                    el.removeEventListener('mouseleave', onMouseLeaveLink);
                });
                attachListeners();
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            observer.disconnect();
            const interactables = document.querySelectorAll('a, button, input, select, textarea');
            interactables.forEach(el => {
                el.removeEventListener('mouseenter', onMouseEnterLink);
                el.removeEventListener('mouseleave', onMouseLeaveLink);
            });
        };
    }, [useCustomCursor]);

    if (!useCustomCursor) return null;

    return (
        <div className="fixed inset-0 pointer-events-none z-[9999]">
            {/* The Outer Ring */}
            <div
                ref={ringRef}
                className="fixed top-0 left-0 w-8 h-8 -ml-4 -mt-4 rounded-full border border-accent bg-transparent pointer-events-none mix-blend-difference"
                style={{ willChange: 'transform' }}
            ></div>
            <div
                ref={dotRef}
                className="fixed top-0 left-0 w-2 h-2 -ml-1 -mt-1 rounded-full bg-accent pointer-events-none"
                style={{ willChange: 'transform' }}
            ></div>
        </div>
    );
}
